import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Typography, Row, Col, Button, Space, notification, Tag, Progress, Spin, Alert, Input } from 'antd';
import { 
  NodeIndexOutlined, 
  DownloadOutlined, 
  EyeOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { DataTable, SummaryCard, DetailDrawer, CommissionIntegration } from '../../components/index.js';
import { exportDataMatch, getSavedHubspotDeals, getSavedWellskyRecords, MatchResultsService } from '../../services/index.js';
import BackendMatchingService from '../../services/BackendMatchingService.js';
import { MatchActions } from '../../types/index.js';

const { Content } = Layout;
const { Title, Paragraph } = Typography;

const MatchDashboard = () => {
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hubspotData, setHubspotData] = useState([]);
  const [wellskyData, setWellskyData] = useState([]);
  const [matchResults, setMatchResults] = useState([]);
  const [matchStats, setMatchStats] = useState({ 
    total: 0, 
    ready: 0, 
    investigation: 0, 
    notEligible: 0, 
    perfectMatches: 0, 
    averageScore: 0 
  });
  const [error, setError] = useState(null);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, percent: 0 });
  
  // New state for persistent results
  const [hasPersistedResults, setHasPersistedResults] = useState(false);
  const [dataFreshness, setDataFreshness] = useState({
    refreshNeeded: false,
    lastMatchedAt: null,
    reason: null
  });
  const [hubspotDataHash, setHubspotDataHash] = useState(null);
  const [wellskyDataHash, setWellskyDataHash] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [searchText, setSearchText] = useState('');

  // Filter match results based on search text
  const filteredMatchResults = useMemo(() => {
    if (!searchText.trim()) return matchResults;
    
    const searchLower = searchText.toLowerCase();
    return matchResults.filter(result => 
      Object.values(result).some(value => 
        value && value.toString().toLowerCase().includes(searchLower)
      )
    );
  }, [matchResults, searchText]);

  // Load data from database
  useEffect(() => {
    const loadData = async () => {
      console.log('🔄 Match Dashboard: Starting data load...');
      setLoading(true);
      setError(null);
      
      try {
        console.log('📡 Match Dashboard: Calling getSavedHubspotDeals and getSavedWellskyRecords...');
        
        // Fetch both datasets from the database - GET ALL RECORDS WITH OPTIMIZED LIMITS
        const [hubspotResponse, wellskyResponse] = await Promise.all([
          getSavedHubspotDeals({ page: 1, limit: 25000 }).then(response => { // Increased for faster processing
            console.log('✅ HubSpot Response:', response);
            return response;
          }).catch(err => {
            console.error('❌ HubSpot Error:', err);
            throw err;
          }),
          getSavedWellskyRecords({ page: 1, limit: 25000 }).then(response => { // Increased for faster processing
            console.log('✅ WellSky Response:', response);
            return response;
          }).catch(err => {
            console.error('❌ WellSky Error:', err);
            throw err;
          })
        ]);

        console.log('📊 Data loaded - HubSpot success:', hubspotResponse?.success, 'WellSky success:', wellskyResponse?.success);
        console.log('📊 HubSpot deals count:', hubspotResponse?.deals?.length || 0);
        console.log('📊 WellSky records count:', wellskyResponse?.records?.length || 0);

        if (hubspotResponse.success && wellskyResponse.success) {
          const hubspotDeals = hubspotResponse.deals || [];
          const wellskyRecords = wellskyResponse.records || [];
          
          console.log('✅ Setting data - HubSpot:', hubspotDeals.length, 'deals, WellSky:', wellskyRecords.length, 'records');
          
          // Warn user if dataset is very large
          if (hubspotDeals.length > 15000 || wellskyRecords.length > 15000) {
            notification.warning({
              message: 'Large Dataset Detected',
              description: `You have ${hubspotDeals.length} HubSpot deals and ${wellskyRecords.length} WellSky records. Matching will process in batches of 250 records for optimal performance.`,
              duration: 8
            });
          }
          
          setHubspotData(hubspotDeals);
          setWellskyData(wellskyRecords);

          // Generate data hashes for change detection
          const hsHash = MatchResultsService.generateDataHash(hubspotDeals);
          const wsHash = MatchResultsService.generateDataHash(wellskyRecords);
          setHubspotDataHash(hsHash);
          setWellskyDataHash(wsHash);

          // Try to load persisted match results and check if refresh is needed
          await loadPersistedResultsAndCheckFreshness(hsHash, wsHash);
        } else {
          const errorMsg = `Failed to load data from database - HubSpot: ${hubspotResponse?.success ? 'OK' : hubspotResponse?.message || 'Failed'}, WellSky: ${wellskyResponse?.success ? 'OK' : wellskyResponse?.message || 'Failed'}`;
          console.error('❌', errorMsg);
          throw new Error(errorMsg);
        }
      } catch (err) {
        console.error('💥 Match Dashboard Error loading data:', err);
        console.error('💥 Error details:', {
          message: err.message,
          stack: err.stack,
          name: err.name
        });
        
        setError(err.message);
        notification.error({
          message: 'Failed to Load Data',
          description: `Could not load HubSpot and WellSky data from the database. Error: ${err.message}. Please ensure you have imported data first.`
        });
      } finally {
        console.log('🏁 Match Dashboard: Data load complete');
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Perform matching using backend service
  const performMatching = async () => {
    if (wellskyData.length === 0 || hubspotData.length === 0) {
      console.log('⚠️ No data available for matching');
      return;
    }

    console.log('🚀 Starting backend matching process...');
    setProcessing(true);
    setBatchProgress({ current: 0, total: 0, percent: 0 });
    
    try {
      // Progress callback for batch processing
      const onProgress = (percent, current, total) => {
        console.log(`📊 PROGRESS DEBUG: ${percent}% - Batch ${current} of ${total}`);
        setBatchProgress({ percent, current, total });
        
        // Show a notification for major milestones
        if (percent === 25 || percent === 50 || percent === 75) {
          notification.info({
            message: 'Matching Progress',
            description: `${percent}% complete - Processing batch ${current} of ${total}`,
            duration: 2
          });
        }
      };

      // Call backend matching service with progress callback
      console.log(`🔢 MATCH DEBUG: About to match ${hubspotData.length} HubSpot deals with ${wellskyData.length} WellSky records using batch size 250`);
      console.log(`🔢 MATCH DEBUG: Expected total batches: ${Math.ceil(hubspotData.length / 250)}`);
      
      const response = await BackendMatchingService.matchRecords(
        hubspotData, 
        wellskyData, 
        250, // Increased batch size for faster processing
        onProgress
      );
      
      // Process the results for the UI
      const processedResults = BackendMatchingService.processMatchingResults(response);
      
      // Convert backend results to the format expected by the UI
      const formattedMatches = processedResults.matches.map((match, index) => {
        // Safely construct fullName for WellSky record
        const getFullName = (record) => {
          if (!record) return null;
          
          const firstName = record.firstName || record.clientFirstName || '';
          const lastName = record.lastName || record.clientLastName || record.clientName || '';
          
          if (firstName && lastName) {
            return `${firstName} ${lastName}`;
          } else if (firstName) {
            return firstName;
          } else if (lastName) {
            return lastName;
          } else if (record.responsiblePartyName) {
            return record.responsiblePartyName;
          } else {
            return 'Name Not Available';
          }
        };

        return {
          key: `match-${index}`,
          hubspotIndex: index,
          wellskyIndex: match.wellskyRecord ? index : null,
          action: determineAction(match),
          investigationReason: (match.investigationReasons && match.investigationReasons.join('; ')) || null,
          matchedCriterion: match.matchedCriterion || 'Unknown',
          score: match.matchScore || 0, // Add score for DetailDrawer
          hubspotRecord: {
            dealName: match.hubspotDeal.dealName || match.hubspotDeal.name,
            fullName: match.hubspotDeal.clientName || match.hubspotDeal.name,
            commissionEligible: match.isEligibleForCommission,
            salesRep: match.hubspotDeal.salesRep || match.hubspotDeal.insideSalesRep || 'Unknown',
            recordType: 'HubSpot',
            // Include all original HubSpot deal properties for DetailDrawer
            ...match.hubspotDeal
          },
          wellskyRecord: match.wellskyRecord ? {
            fullName: getFullName(match.wellskyRecord),
            ...match.wellskyRecord
          } : null,
          originalMatch: match
        };
      });

      // Add unmatched records
      const formattedUnmatched = processedResults.unmatched.map((unmatched, index) => ({
        key: `unmatched-${index}`,
        hubspotIndex: processedResults.matches.length + index,
        wellskyIndex: null,
        action: MatchActions.NOT_ELIGIBLE,
        investigationReason: unmatched.reason,
        matchedCriterion: 'No Match',
        score: 0,
        hubspotRecord: {
          dealName: unmatched.hubspotDeal.dealName || unmatched.hubspotDeal.name || '',
          fullName: unmatched.hubspotDeal.clientName || unmatched.hubspotDeal.name || '',
          commissionEligible: false,
          salesRep: unmatched.hubspotDeal.salesRep || unmatched.hubspotDeal.insideSalesRep || 'Unknown',
          recordType: 'HubSpot (Unmatched)',
          // Include all original HubSpot deal properties for DetailDrawer
          ...unmatched.hubspotDeal
        },
        wellskyRecord: null,
        originalMatch: null
      }));

      const allResults = [...formattedMatches, ...formattedUnmatched];
      
      // Calculate stats
      const stats = {
        total: allResults.length,
        ready: allResults.filter(r => r.action === MatchActions.READY).length,
        investigation: allResults.filter(r => r.action === MatchActions.INVESTIGATION).length,
        notEligible: allResults.filter(r => r.action === MatchActions.NOT_ELIGIBLE).length,
        perfectMatches: allResults.filter(r => r.score >= 125).length, // Backend max score is 125 (100%)
        averageScore: allResults.length > 0 ? Math.round((allResults.reduce((sum, r) => sum + (r.score || 0), 0) / allResults.length) / 125 * 100) : 0
      };

      setMatchResults(allResults);
      setMatchStats(stats);

      console.log('✅ Backend matching completed successfully');
      console.log(`📊 Results: ${stats.total} total, ${stats.ready} ready, ${stats.investigation} investigation, ${stats.notEligible} not eligible`);

      // Save match results to backend for persistence
      try {
        console.log('💾 Saving match results for persistence...');
        const saveResponse = await MatchResultsService.saveMatchResults(response.data, hubspotDataHash, wellskyDataHash);
        
        // Store the session ID for commission generation
        if (saveResponse.data && saveResponse.data.sessionId) {
          setCurrentSessionId(saveResponse.data.sessionId);
          console.log('📋 Session ID stored:', saveResponse.data.sessionId);
        }
        
        // Update data freshness state
        setDataFreshness({
          refreshNeeded: false,
          lastMatchedAt: new Date().toISOString(),
          reason: 'Data is up to date'
        });
        setHasPersistedResults(true);
        
        console.log('✅ Match results saved successfully');
      } catch (saveError) {
        console.error('❌ Failed to save match results:', saveError);
        // Don't show error to user, matching was successful
      }

      notification.success({
        message: 'Matching Complete',
        description: `Successfully matched ${stats.total} records using backend processing.`
      });

    } catch (error) {
      console.error('❌ Backend matching failed:', error);
      notification.error({
        message: 'Matching Failed',
        description: `Failed to perform matching on backend: ${error.message}`
      });
    } finally {
      setProcessing(false);
    }
  };

  // Helper function to determine action based on match criteria
  const determineAction = (match) => {
    if (!match.isEligibleForCommission) {
      return MatchActions.NOT_ELIGIBLE;
    }
    
    if (match.investigationReasons && match.investigationReasons.length > 0) {
      return MatchActions.INVESTIGATION;
    }
    
    // In boolean matching, if there's a match and it's eligible, it's ready
    return MatchActions.READY;
  };

  // NOTE: Auto-matching has been disabled to prevent unwanted background processing
  // Users must manually click the "Run Matching" button to start the process
  // 
  // Previously: useEffect(() => {
  //   if (hubspotData.length > 0 && wellskyData.length > 0 && matchResults.length === 0) {
  //     performMatching();
  //   }
  // }, [hubspotData, wellskyData]);

  // Define columns for match results table
  const matchColumns = [
    {
      title: 'Action Required',
      dataIndex: 'action',
      key: 'action',
      width: 160,
      render: (action) => {
        const colors = {
          [MatchActions.READY]: 'green',
          [MatchActions.INVESTIGATION]: 'orange',
          [MatchActions.NOT_ELIGIBLE]: 'red'
        };
        const icons = {
          [MatchActions.READY]: <CheckCircleOutlined />,
          [MatchActions.INVESTIGATION]: <ExclamationCircleOutlined />,
          [MatchActions.NOT_ELIGIBLE]: <CloseCircleOutlined />
        };
        return (
          <Tag color={colors[action]} icon={icons[action]}>
            {action}
          </Tag>
        );
      },
      filters: [
        { text: MatchActions.READY, value: MatchActions.READY },
        { text: MatchActions.INVESTIGATION, value: MatchActions.INVESTIGATION },
        { text: MatchActions.NOT_ELIGIBLE, value: MatchActions.NOT_ELIGIBLE }
      ],
      onFilter: (value, record) => record.action === value
    },
    {
      title: 'Investigation Reason',
      dataIndex: 'investigationReason',
      key: 'investigationReason',
      width: 200,
      render: (reason) => reason || '-'
    },
    {
      title: 'Commission Eligible',
      key: 'commissionEligible',
      width: 140,
      render: (_, record) => {
        const eligible = record.hubspotRecord?.commissionEligible;
        return (
          <Tag color={eligible ? 'green' : 'red'}>
            {eligible ? 'Yes' : 'No'}
          </Tag>
        );
      },
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false }
      ],
      onFilter: (value, record) => record.hubspotRecord?.commissionEligible === value
    },
    {
      title: 'HubSpot Deal',
      key: 'hubspotDeal',
      width: 200,
      render: (_, record) => {
        // Only show deal name if there's a match (has a WellSky record)
        if (record.wellskyRecord && record.hubspotRecord?.dealName) {
          return (
            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
              {record.hubspotRecord.dealName}
            </span>
          );
        }
        return <span style={{ color: '#d9d9d9', fontStyle: 'italic' }}>No Match</span>;
      }
    },
    {
      title: 'Record Type',
      key: 'recordType',
      width: 120,
      render: (_, record) => {
        const type = record.hubspotRecord?.recordType || 'Unknown';
        const color = type.includes('Unmatched') ? 'orange' : 'blue';
        return <Tag color={color}>{type}</Tag>;
      }
    },
    {
      title: 'WellSky Name',
      key: 'wellskyName',
      width: 180,
      render: (_, record) => record.wellskyRecord?.fullName || '-'
    },
    {
      title: 'HubSpot Name',
      key: 'hubspotName',
      width: 180,
      render: (_, record) => record.hubspotRecord?.fullName || '-'
    },
    {
      title: 'Sales Rep',
      key: 'salesRep',
      width: 150,
      render: (_, record) => record.hubspotRecord?.salesRep || '-'
    },
    {
      title: 'Match Score',
      dataIndex: 'score',
      key: 'score',
      width: 100,
      render: (score) => {
        // Backend uses 0-125 point scale, convert to 0-100 percentage
        const maxScore = 125; // Database ID (50) + Name (30) + Phone (20) + Email (15) + Address (10)
        const percentage = Math.min(Math.round((score / maxScore) * 100), 100);
        
        return (
          <div style={{ textAlign: 'center' }}>
            <Progress
              type="circle"
              percent={percentage}
              size={40}
              format={() => `${percentage}%`}
              strokeColor={percentage >= 80 ? '#52c41a' : percentage >= 40 ? '#faad14' : '#ff4d4f'}
            />
          </div>
        );
      },
      sorter: (a, b) => a.score - b.score
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
          style={{
            borderRadius: 8,
            height: 32,
            paddingLeft: 12,
            paddingRight: 12,
            fontWeight: 500,
            border: '1px solid #d9d9d9',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            '&:hover': {
              borderColor: '#40a9ff',
              color: '#40a9ff'
            }
          }}
        >
          View
        </Button>
      )
    }
  ];

  const handleViewDetails = (matchResult) => {
    setSelectedMatch(matchResult);
    setDrawerVisible(true);
  };

  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    setSelectedMatch(null);
  };

  const handleExportMatches = () => {
    try {
      exportDataMatch(matchResults);
      
      notification.success({
        message: 'Export Successful',
        description: 'Match results exported successfully.',
        placement: 'topRight'
      });
    } catch (error) {
      notification.error({
        message: 'Export Failed',
        description: error.message,
        placement: 'topRight'
      });
    }
  };

  // Load persisted match results and check data freshness
  const loadPersistedResultsAndCheckFreshness = async (hubspotHash, wellskyHash) => {
    try {
      console.log('🔍 Checking for persisted match results...');

      // Try to load the latest match results
      const savedResults = await MatchResultsService.getLatestMatchResults();
      
      if (savedResults.success && savedResults.data.hasResults) {
        console.log('✅ Found persisted match results');
        
        // Store the session ID from persisted results
        if (savedResults.data.session && savedResults.data.session.id) {
          setCurrentSessionId(savedResults.data.session.id);
          console.log('📋 Session ID loaded from persisted results:', savedResults.data.session.id);
        }
        
        // Check if data has changed since last match
        const refreshCheck = await MatchResultsService.checkRefreshNeeded(hubspotHash, wellskyHash);
        
        setDataFreshness({
          refreshNeeded: refreshCheck.data.refreshNeeded,
          lastMatchedAt: refreshCheck.data.lastMatchedAt,
          reason: refreshCheck.data.reason
        });

        // If data hasn't changed, load the saved results
        if (!refreshCheck.data.refreshNeeded) {
          console.log('📂 Loading saved match results (data is fresh)');
          
          const processedResults = MatchResultsService.processSavedMatchResults(savedResults);
          
          // Convert to UI format (same as performMatching)
          const { formattedMatches, formattedUnmatched, stats } = await formatMatchResultsForUI(processedResults);
          
          setMatchResults([...formattedMatches, ...formattedUnmatched]);
          setMatchStats(stats);
          setHasPersistedResults(true);

          notification.success({
            message: 'Loaded Previous Results',
            description: `Loaded ${stats.total} previous match results. Data is up to date as of ${new Date(refreshCheck.data.lastMatchedAt).toLocaleString()}.`,
            duration: 6
          });
        } else {
          console.log('⚠️ Data has changed, refresh needed');
          setHasPersistedResults(false);
          
          notification.info({
            message: 'Data Update Detected',
            description: `${refreshCheck.data.reason}. Previous results are available, but you may want to re-run matching to get updated results.`,
            duration: 8
          });
        }
      } else {
        console.log('ℹ️ No persisted match results found');
        setHasPersistedResults(false);
        setDataFreshness({
          refreshNeeded: true,
          lastMatchedAt: null,
          reason: 'No previous matching sessions found'
        });
      }
    } catch (error) {
      console.error('❌ Error loading persisted results:', error);
      // Don't show error to user, just continue without persisted results
      setHasPersistedResults(false);
      setDataFreshness({
        refreshNeeded: true,
        lastMatchedAt: null,
        reason: 'Error loading previous results'
      });
    }
  };

  // Format match results for UI display (extracted from performMatching)
  const formatMatchResultsForUI = async (processedResults) => {
    const { matches, unmatched } = processedResults;

    // Convert matches to UI format
    const formattedMatches = matches.map((match, index) => {
      // Safely construct fullName for WellSky record
      const getFullName = (record) => {
        if (!record) return null;
        
        const firstName = record.firstName || record.clientFirstName || '';
        const lastName = record.lastName || record.clientLastName || record.clientName || '';
        
        if (firstName && lastName) {
          return `${firstName} ${lastName}`;
        } else if (firstName) {
          return firstName;
        } else if (lastName) {
          return lastName;
        } else if (record.responsiblePartyName) {
          return record.responsiblePartyName;
        } else {
          return 'Name Not Available';
        }
      };

      return {
        key: `match-${index}`,
        hubspotIndex: index,
        wellskyIndex: match.bestMatch?.wellskyRecord ? index : null,
        action: determineAction(match.bestMatch || match),
        investigationReason: (match.investigationReasons && match.investigationReasons.join('; ')) || null,
        matchedCriterion: match.matchedCriteria?.join(', ') || 'Unknown',
        score: match.totalScore || 0,
        hubspotRecord: {
          dealName: match.hubspotDeal.dealName || match.hubspotDeal.name || '',
          fullName: match.hubspotDeal.clientName || match.hubspotDeal.name || '',
          commissionEligible: match.bestMatch?.isEligibleForCommission !== false,
          salesRep: match.hubspotDeal.salesRep || match.hubspotDeal.insideSalesRep || 'Unknown',
          recordType: 'HubSpot',
          ...match.hubspotDeal
        },
        wellskyRecord: match.bestMatch?.wellskyRecord ? {
          fullName: getFullName(match.bestMatch.wellskyRecord),
          ...match.bestMatch.wellskyRecord
        } : null,
        originalMatch: match.bestMatch || match
      };
    });

    // Convert unmatched to UI format
    const formattedUnmatched = unmatched.map((unmatchedRecord, index) => ({
      key: `unmatched-${index}`,
      hubspotIndex: formattedMatches.length + index,
      wellskyIndex: null,
      action: MatchActions.NOT_ELIGIBLE,
      investigationReason: unmatchedRecord.reason,
      matchedCriterion: 'No Match',
      score: 0,        hubspotRecord: {
          dealName: unmatchedRecord.hubspotDeal.dealName || unmatchedRecord.hubspotDeal.clientName || unmatchedRecord.hubspotDeal.name || '',
          fullName: unmatchedRecord.hubspotDeal.clientName || unmatchedRecord.hubspotDeal.name || '',
          commissionEligible: false,
          salesRep: unmatchedRecord.hubspotDeal.salesRep || unmatchedRecord.hubspotDeal.insideSalesRep || 'Unknown',
          recordType: 'HubSpot (Unmatched)',
          ...unmatchedRecord.hubspotDeal
        },
      wellskyRecord: null,
      originalMatch: null
    }));

    const allResults = [...formattedMatches, ...formattedUnmatched];
    
    // Calculate stats
    const stats = {
      total: allResults.length,
      ready: allResults.filter(r => r.action === MatchActions.READY).length,
      investigation: allResults.filter(r => r.action === MatchActions.INVESTIGATION).length,
      notEligible: allResults.filter(r => r.action === MatchActions.NOT_ELIGIBLE).length,
      perfectMatches: allResults.filter(r => r.score >= 125).length,
      averageScore: allResults.length > 0 ? Math.round((allResults.reduce((sum, r) => sum + (r.score || 0), 0) / allResults.length) / 125 * 100) : 0
    };

    return { formattedMatches, formattedUnmatched, stats };
  };

  // Show loading state
  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <Content style={{ padding: '24px' }}>
          <div style={{ margin: '0 auto', textAlign: 'center', paddingTop: '10%' }}>
            <Spin size="large" />
            <Title level={3} type="secondary" style={{ marginTop: 16 }}>Loading Data...</Title>
            <Paragraph type="secondary">
              Fetching HubSpot and WellSky data from the database...
            </Paragraph>
          </div>
        </Content>
      </Layout>
    );
  }

  // Show error state
  if (error) {
    return (
      <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <Content style={{ padding: '24px' }}>
          <div style={{ margin: '0 auto', textAlign: 'center', paddingTop: '10%' }}>
            <Alert
              message="Failed to Load Data"
              description={error}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Button 
              type="primary" 
              icon={<ReloadOutlined />}
              onClick={() => window.location.reload()}
              size="large"
              style={{
                borderRadius: 12,
                height: 48,
                paddingLeft: 24,
                paddingRight: 24,
                fontWeight: 500,
                boxShadow: '0 4px 12px rgba(255, 77, 79, 0.3)',
                background: 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)',
                border: 'none'
              }}
            >
              Retry
            </Button>
          </div>
        </Content>
      </Layout>
    );
  }

  // Show no data state
  if (wellskyData.length === 0 || hubspotData.length === 0) {
    return (
      <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <Content style={{ padding: '24px' }}>
          <div style={{ margin: '0 auto', textAlign: 'center', paddingTop: '10%' }}>
            <NodeIndexOutlined style={{ fontSize: 64, color: '#d9d9d9', marginBottom: 16 }} />
            <Title level={3} type="secondary">No Data to Match</Title>
            <Paragraph type="secondary">
              Please import both WellSky and HubSpot data before proceeding to matching.
              <br />
              Current data: {hubspotData.length} HubSpot deals, {wellskyData.length} WellSky records
            </Paragraph>
            <Button 
              type="primary" 
              href="/import"
              size="large"
              style={{
                borderRadius: 12,
                height: 48,
                paddingLeft: 24,
                paddingRight: 24,
                fontWeight: 500,
                boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
                background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
                border: 'none'
              }}
            >
              Go to Import Page
            </Button>
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Content style={{ padding: '24px' }}>
        <div style={{ margin: '0 auto' }}>
          {/* Action Bar */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                {dataFreshness.lastMatchedAt && (
                  <Paragraph type="secondary" style={{ marginTop: 8, fontSize: '12px' }}>
                    <span style={{ color: dataFreshness.refreshNeeded ? '#faad14' : '#52c41a' }}>
                      {dataFreshness.refreshNeeded ? '⚠️' : '✅'} 
                    </span>
                    {dataFreshness.refreshNeeded 
                      ? ` Data update detected - ${dataFreshness.reason}` 
                      : ` Data is up to date`
                    }
                    {dataFreshness.lastMatchedAt && ` • Last matched: ${new Date(dataFreshness.lastMatchedAt).toLocaleString()}`}
                  </Paragraph>
                )}
              </div>
              <Space size={16}>
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={performMatching}
                  loading={processing}
                  disabled={hubspotData.length === 0 || wellskyData.length === 0}
                  title="Re-run matching process"
                  type={dataFreshness.refreshNeeded ? 'primary' : 'default'}
                  size="large"
                  style={{
                    borderRadius: 12,
                    height: 48,
                    paddingLeft: 24,
                    paddingRight: 24,
                    fontWeight: 500,
                    boxShadow: dataFreshness.refreshNeeded ? '0 4px 12px rgba(24, 144, 255, 0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                    border: dataFreshness.refreshNeeded ? 'none' : '1px solid #d9d9d9'
                  }}
                >
                  {processing ? 'Matching...' : dataFreshness.refreshNeeded ? 'Refresh Match' : 'Re-match'}
                </Button>
                <Button 
                  icon={<DownloadOutlined />}
                  onClick={handleExportMatches}
                  disabled={matchResults.length === 0}
                  type="primary"
                  size="large"
                  style={{
                    borderRadius: 12,
                    height: 48,
                    paddingLeft: 24,
                    paddingRight: 24,
                    fontWeight: 500,
                    boxShadow: '0 4px 12px rgba(82, 196, 26, 0.3)',
                    background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                    border: 'none'
                  }}
                >
                  Export Matches
                </Button>
              </Space>
            </div>
          </div>

          {/* Summary Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                title="Total Matches"
                value={matchStats.total}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                title="Ready for Commission"
                value={matchStats.ready}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                title="Need Investigation"
                value={matchStats.investigation}
                valueStyle={{ color: '#faad14' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                title="Not Eligible"
                value={matchStats.notEligible}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Col>
          </Row>

          {/* Additional Stats */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12}>
              <SummaryCard
                title="Perfect Matches"
                value={matchStats.perfectMatches}
                suffix="(100%)"
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col xs={24} sm={12}>
              <SummaryCard
                title="Average Match Score"
                value={matchStats.averageScore}
                suffix="%"
                precision={0}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
          </Row>

          {/* Match Results Table */}
          <div style={{ backgroundColor: 'white', borderRadius: 8, padding: 24 }}>
            <Title level={4} style={{ marginBottom: 16 }}>
              Match Results ({matchResults.length} records)
            </Title>
            
            {processing ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Progress 
                  type="circle" 
                  percent={batchProgress.percent} 
                  format={percent => `${percent}%`}
                />
                <Paragraph style={{ marginTop: 16 }}>
                  Processing matches...
                  {batchProgress.total > 0 && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: 8 }}>
                      Batch {batchProgress.current} of {batchProgress.total}
                    </div>
                  )}
                </Paragraph>
              </div>
            ) : matchResults.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <NodeIndexOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                <Title level={4} type="secondary">
                  {hasPersistedResults ? 'No Recent Match Results' : 'Ready to Start Matching'}
                </Title>
                <Paragraph type="secondary" style={{ marginBottom: 24 }}>
                  You have {hubspotData.length} HubSpot deals and {wellskyData.length} WellSky records ready to match.
                  <br />
                  {hasPersistedResults 
                    ? 'Previous results were found but could not be loaded. Click below to run a fresh match.'
                    : 'Click the button below to begin the matching process.'
                  }
                  {dataFreshness.refreshNeeded && dataFreshness.lastMatchedAt && (
                    <>
                      <br />
                      <small style={{ color: '#faad14' }}>
                        Data has been updated since your last match on {new Date(dataFreshness.lastMatchedAt).toLocaleDateString()}.
                      </small>
                    </>
                  )}
                </Paragraph>
                <Button 
                  type="primary"
                  size="large"
                  icon={<ReloadOutlined />}
                  onClick={performMatching}
                  loading={processing}
                  style={{
                    borderRadius: 12,
                    height: 56,
                    paddingLeft: 32,
                    paddingRight: 32,
                    fontWeight: 600,
                    fontSize: '16px',
                    boxShadow: '0 6px 16px rgba(24, 144, 255, 0.4)',
                    background: dataFreshness.refreshNeeded 
                      ? 'linear-gradient(135deg, #faad14 0%, #ffc53d 100%)' 
                      : 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
                    border: 'none'
                  }}
                >
                  {dataFreshness.refreshNeeded ? 'Refresh Match Results' : 'Start Matching Process'}
                </Button>
              </div>
            ) : (
              <>
                {/* Search and Generate Commission Row */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: 16,
                  padding: '0 4px'
                }}>
                  <div style={{ flex: 1, maxWidth: 400 }}>
                    <Input
                      placeholder="Search all columns..."
                      allowClear
                      prefix={<SearchOutlined />}
                      size="large"
                      value={searchText}
                      style={{ 
                        borderRadius: 12,
                        height: 48,
                        fontSize: '14px',
                        border: '1px solid #d9d9d9',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        transition: 'all 0.3s ease'
                      }}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  </div>
                  <CommissionIntegration 
                    matchResults={matchResults}
                    sessionId={currentSessionId}
                    disabled={matchResults.length === 0}
                  />
                </div>
                <DataTable
                  columns={matchColumns}
                  data={filteredMatchResults}
                  pageSize={25}
                  bordered
                  size="small"
                  onRowClick={handleViewDetails}
                  showSearch={false}
                />
              </>
            )}
          </div>

          {/* Detail Drawer */}
          <DetailDrawer
            visible={drawerVisible}
            onClose={handleCloseDrawer}
            matchResult={selectedMatch}
            title="Match Details"
          />
        </div>
      </Content>
    </Layout>
  );
};

export default MatchDashboard;
